import { sysadminService, type GoogleJobLog } from '@/services/sysadmin.service';
import { AlertCircle, Server } from 'lucide-react';
import { BackgroundJobProcessor } from '@/components/BackgroundJobProcessor';
import { useEffect, useState } from 'react';

export function MonitoreoLogsPage() {
  const [logs, setLogs] = useState<GoogleJobLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await sysadminService.listarGoogleJobs();
        if (response.success) {
          setLogs(response.googleJobs);
        } else {
          setError('No fue posible obtener los logs.');
        }
      } catch (err: any) {
        console.error('Error al cargar logs:', err);
        setError(err.message || 'Error al obtener los logs del sistema.');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 font-medium">Cargando logs del sistema...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Componente de Prueba de Trabajos en Segundo Plano */}
      <BackgroundJobProcessor />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Server className="w-5 h-5 text-gray-500" />
              Logs del Sistema
            </h2>
            <p className="text-sm text-gray-500 mt-1">Monitoreo de tareas en segundo plano y webhooks.</p>
          </div>
        </div>

        {error ? (
          <div className="p-6">
            <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 font-medium text-gray-900">Fecha</th>
                  <th className="px-6 py-3 font-medium text-gray-900">Servicio / Operación</th>
                  <th className="px-6 py-3 font-medium text-gray-900">Estado</th>
                  <th className="px-6 py-3 font-medium text-gray-900">Payload</th>
                  <th className="px-6 py-3 font-medium text-gray-900">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No se encontraron registros en el sistema.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 whitespace-nowrap">{new Date(log.createdAt).toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{log.service}</div>
                        <div className="text-xs text-gray-500">{log.operation}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                            log.status === 'SUCCEEDED'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : log.status === 'FAILED'
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : log.status === 'IN_PROGRESS'
                                  ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                  : 'bg-gray-50 text-gray-700 border-gray-200'
                          }`}
                        >
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <pre className="text-[10px] bg-gray-50 p-2 rounded border border-gray-200 whitespace-pre-wrap wrap-break-word min-w-50 max-w-md">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      </td>
                      <td className="px-6 py-4">
                        {log.lastError ? (
                          <div className="text-xs text-red-600 whitespace-normal wrap-break-word min-w-37.5">{log.lastError}</div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
